import React, { useState, useEffect } from "react";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDate, useStep } from "../contexts/SubscriptionContext";
import { useGeneral } from "../contexts/GeneralContext";

const DateCustomization = ({ numberOfDays = 1 }) => {
  const { nextStep, prevStep } = useStep();
  const { extraContent } = useGeneral();
  const { selectedDate, setSelectedDate } = useDate();
  const navigate = useNavigate();
  let currentStartDate = extraContent?.todaysDate;
  // Calculate initial date: +48 hours from currentStartDate, skip weekends to Monday
const calculateInitialDate = (baseDate) => {
  // Extract ONLY the date portion from server — ignore time & timezone
  const getServerDate = (d) => {
    if (!d) return new Date(); // fallback to now

    // "2026-04-29T20:00:00Z" or "2026-04-29" → always use "2026-04-29"
    const iso = (d instanceof Date ? d.toISOString() : d).split("T")[0];
    const [year, month, day] = iso.split("-").map(Number);

    // Build at local midnight using ONLY server's date
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  const base = getServerDate(baseDate);

  // +48 hours = 2 days forward from the server date
  const date48Hours = new Date(base);
  date48Hours.setDate(base.getDate() + 2);

  // Skip weekends
  const dayOfWeek = date48Hours.getDay();
  if (dayOfWeek === 6) date48Hours.setDate(date48Hours.getDate() + 2); // Sat → Mon
  else if (dayOfWeek === 0) date48Hours.setDate(date48Hours.getDate() + 1); // Sun → Mon

  return date48Hours;
};

  // Initialize startDate: always +48h from currentStartDate prop
  const getInitialStartDate = () => {
    return calculateInitialDate(currentStartDate);
  };

  const [startDate, setStartDate] = useState(getInitialStartDate());
  // Always base currentMonth on the initial calculated date, not the selected date
  const [currentMonth, setCurrentMonth] = useState(
    new Date(calculateInitialDate(currentStartDate)),
  );
  const [selectableDateRange, setSelectableDateRange] = useState([]);
  const [showTwoMonths, setShowTwoMonths] = useState(false);
  const [userHasSelectedDate, setUserHasSelectedDate] = useState(false);

  // Sync startDate whenever currentStartDate changes (e.g. user returns next day).
  // If a date was previously selected but is now before the new minimum, reset to new minimum.
  useEffect(() => {
    const initialDate = calculateInitialDate(currentStartDate);

    const currentSelected = selectedDate
      ? typeof selectedDate === "string"
        ? new Date(selectedDate)
        : selectedDate
      : null;

    const isSelectedStillValid =
      currentSelected &&
      new Date(
        currentSelected.getFullYear(),
        currentSelected.getMonth(),
        currentSelected.getDate(),
      ) >=
        new Date(
          initialDate.getFullYear(),
          initialDate.getMonth(),
          initialDate.getDate(),
        );

    if (isSelectedStillValid) {
      // Previously selected date is still valid — keep it
      setStartDate(currentSelected);
    } else {
      // No prior selection or it's now in the past — reset to new minimum
      setStartDate(initialDate);
      setSelectedDate(initialDate);
      setUserHasSelectedDate(false);
    }
  }, [currentStartDate]);

  // Guard: if the currently displayed startDate has fallen behind today's minimum
  // (e.g. the user left the tab open overnight, or context restored a stale date),
  // snap it forward to the next valid date.
  useEffect(() => {
    const initialDate = calculateInitialDate(currentStartDate);

    const normalise = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());

    if (normalise(startDate) < normalise(initialDate)) {
      setStartDate(initialDate);
      setSelectedDate(initialDate);
      setUserHasSelectedDate(false);
    }
  }, [startDate, currentStartDate]);

  // Real-time guard: poll every 60s against the actual system clock.
  // This catches cases where currentStartDate prop hasn't changed (e.g. server
  // value is stale) but the real date has moved forward, making the selected
  // date invalid (e.g. user had set system date to future, then restored it).
  useEffect(() => {
    const check = () => {
      // Always derive minimum from real wall-clock "now", not from the prop
      const realNowBase = new Date();
      const realMinDate = calculateInitialDate(realNowBase);

      const normalise = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());

      if (normalise(startDate) < normalise(realMinDate)) {
        setStartDate(realMinDate);
        setSelectedDate(realMinDate);
        setUserHasSelectedDate(false);
      }
    };

    // Run immediately on mount / whenever startDate changes
    check();

    const interval = setInterval(check, 60 * 1000); // re-check every 60 seconds
    return () => clearInterval(interval);
  }, [startDate]);

  // Calculate the range of selectable dates based on mealSubscriptionStartDateAllowedDays
  useEffect(() => {
    const initialDate = calculateInitialDate(currentStartDate);
    const availableDays =
      extraContent?.mealSubscriptionStartDateAllowedDays || 1;

    if (availableDays > 0) {
      const dates = [];
      let current = new Date(initialDate);
      let addedDays = 0;

      while (addedDays < availableDays) {
        const dayOfWeek = current.getDay();

        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          dates.push(new Date(current));
          addedDays++;
        }

        current.setDate(current.getDate() + 1);
      }

      setSelectableDateRange(dates);

      // Check if dates span across 2 months
      if (dates.length > 0) {
        const firstMonth = dates[0].getMonth();
        const lastMonth = dates[dates.length - 1].getMonth();
        setShowTwoMonths(firstMonth !== lastMonth);
      }
    }
  }, [extraContent?.mealSubscriptionStartDateAllowedDays,extraContent?.todaysDate]);

  // Min selectable date is always +48h from currentStartDate
  const parseCurrentStartDate = () => {
    return calculateInitialDate(currentStartDate);
  };

  const minSelectableDate = parseCurrentStartDate();

  // Helper function to format date for input (yyyy-mm-dd) without timezone issues
  const formatDateForInput = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Handle start date change
  const handleStartDateChange = (e) => {
    const [year, month, day] = e.target.value.split("-").map(Number);
    const newDate = new Date(year, month - 1, day);

    // Check if it's a weekend
    const dayOfWeek = newDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return; // Don't allow weekend selection
    }

    // Check if it's within the selectable range
    if (!isDateInSelectableRange(newDate)) {
      return;
    }

    setStartDate(newDate);
    setSelectedDate(newDate); // Save to context
    setUserHasSelectedDate(true); // Mark as user selected
    // Don't update currentMonth - keep it based on initial date
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  // Check if date is before currentStartDate (minimum selectable date)
  const isBeforeMinDate = (date) => {
    if (!date) return false;
    const compareDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const minDate = new Date(
      minSelectableDate.getFullYear(),
      minSelectableDate.getMonth(),
      minSelectableDate.getDate(),
    );
    return compareDate < minDate;
  };

  // Check if date is within the selectable range
  const isDateInSelectableRange = (date) => {
    if (!date) return false;

    // If no range is set or mealSubscriptionStartDateAllowedDays is 0, allow all dates
    if (!selectableDateRange || selectableDateRange.length === 0) {
      return true;
    }

    return selectableDateRange.some(
      (selectableDate) => selectableDate.toDateString() === date.toDateString(),
    );
  };

  // Handle date click
  const handleDateClick = (date) => {
    if (
      !date ||
      isWeekend(date) ||
      isBeforeMinDate(date) ||
      !isDateInSelectableRange(date)
    ) {
      return;
    }
    setStartDate(new Date(date));
    setSelectedDate(new Date(date)); // Save to context
    setUserHasSelectedDate(true); // Mark as user selected
    // Don't update currentMonth - keep it based on initial date
  };

  // Generate calendar grid for a specific month
  const generateCalendarGrid = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const calendarDays = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(new Date(year, month, day));
    }

    return calendarDays;
  };

  // Check if a date is the selected start date
  const isDateSelected = (date) => {
    if (!date || !startDate) return false;
    return date.toDateString() === startDate.toDateString();
  };

  // Check if date is a weekend
  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Get the first and second month data
  const firstMonthYear = currentMonth.getFullYear();
  const firstMonthIndex = currentMonth.getMonth();

  const secondMonth = new Date(firstMonthYear, firstMonthIndex + 1, 1);
  const secondMonthYear = secondMonth.getFullYear();
  const secondMonthIndex = secondMonth.getMonth();

  const firstMonthDays = generateCalendarGrid(firstMonthYear, firstMonthIndex);
  const secondMonthDays = generateCalendarGrid(
    secondMonthYear,
    secondMonthIndex,
  );

  const firstMonthName = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const secondMonthName = secondMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Render a single month calendar
  const renderMonth = (monthDays, monthName) => (
    <div className="bg-[#CFD4CD]/20 rounded-md shadow-md p-3">
      <h3 className="text-quaternary text-lg md:text-xl text-center mb-3 text-gray-700">
        {monthName}
      </h3>

      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {/* Day headers */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-gray-600 py-1 md:py-2 text-xs md:text-sm"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {monthDays.map((date, index) => {
          const isSelected = isDateSelected(date);
          const isWeekendDay = isWeekend(date);
          const isBeforeMin = isBeforeMinDate(date);
          const isOutsideRange = !isDateInSelectableRange(date);
          const isDisabled =
            !date || isWeekendDay || isBeforeMin || isOutsideRange;

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              disabled={isDisabled}
              className={`max-h-[50px] w-full
                aspect-square flex items-center justify-center rounded-lg text-xs md:text-sm font-medium
                transition-all duration-200
                ${!date ? "bg-transparent cursor-default" : ""}
                ${date && (isBeforeMin || isOutsideRange) ? "bg-gray/10 opacity-50 text-gray-400 cursor-not-allowed" : isWeekendDay ? "bg-gray/70 text-gray-400 cursor-not-allowed" : ""}
                ${date && !isWeekendDay && !isSelected && !isBeforeMin && !isOutsideRange ? "bg-gray/50 hover:bg-quaternary/40 text-gray-700 cursor-pointer hover:scale-105" : ""}
                ${date && isSelected ? "bg-quaternary/100 text-white font-semibold shadow-md" : ""}
              `}
            >
              {date ? date.getDate() : ""}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <motion.div
        initial={{ x: -200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -200, opacity: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-1 2xl:grid-cols-[2fr_1fr] gap-8 items-start">
          <div>
            <p className="text-34pt pb-4 lg:pb-9">Delivery Starting Date</p>

            <div className="mb-6 p-4 bg-orange/10 rounded-lg">
              <p className="text-lg text-gray-700">
                {!userHasSelectedDate
                  ? "Delivery Starts on"
                  : "Selected Start Date"}
                :{" "}
                <span className="text-orange font-semibold">
                  {startDate.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </p>
              {extraContent?.mealSubscriptionStartDateAllowedDays > 0 && (
                <p className="text-sm text-gray-600 mt-2 font-mono">
                  You can select from{" "}
                  {extraContent.mealSubscriptionStartDateAllowedDays} available
                  weekdays starting from the default date. 
                </p>
              )}
            </div>

            <div className="mx-auto">
              {/* Two Month Calendar Grid or Single Month */}
              <div
                className={`grid ${showTwoMonths ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"} gap-4 mb-4`}
              >
                {renderMonth(firstMonthDays, firstMonthName)}
                {showTwoMonths && renderMonth(secondMonthDays, secondMonthName)}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col xs:flex-row gap-5 items-center justify-between mt-8 lg:mt-10">
              <GeneralButton
                onClick={() => {
                  navigate(-1);
                }}
                text="Prev Step"
                title=""
                iconPosition="Left"
                bgColor="#ccc"
                textColor="#000"
                icon={<TiChevronLeft size={24} />}
                className="text-24pt"
              />
              <GeneralButton
                onClick={() => {
                  nextStep();
                  navigate("/subscribe/disclaimer");
                }}
                text="Next Step"
                title=""
                bgColor="#30160E"
                textColor="#ccc"
                icon={<TiChevronRight size={24} />}
                className="text-24pt"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default DateCustomization;
